import Badge from '@components/Badge';
import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { SentGiftBoxRow } from '@models/gift';
import { useFlow } from '@stackflow/react';
import { useTranslation } from 'react-i18next';

import { formatGiftDate, formatSentReceivers } from '../../utils';

interface Props {
    gift: SentGiftBoxRow;
}

function SentGiftItem({ gift }: Props) {
    const { t } = useTranslation('gift');
    const { push } = useFlow();
    const receiver = formatSentReceivers(
        gift.receiverNames,
        (count) => t('giftBox.others', { count }),
    );

    const statusLabel = (() => {
        switch (gift.status) {
        case 'AVAILABLE':
            return t('giftBox.status.beforeUse');
        case 'USED':
            return t('giftBox.status.used');
        case 'CANCELED':
            return t('giftBox.status.canceledDone');
        case 'EXPIRED':
            return t('giftBox.status.expired');
        default:
            return null;
        }
    })();

    const handleClick = () => {
        push('GiftSentActivity', { giftOrderNo: gift.giftOrderNo });
    };

    return (
        <Item type='button' onClick={handleClick}>
            <Thumb src={gift.imageUrl} width={100} height={100} alt={gift.giftItemName} />
            <Info fullWidth spacing={2} align='flex-start'>
                <Text size='body-short' color={colors.textThird} fontWeight='regular'>
                    {t('giftBox.toPrefix', { name: receiver || '-' })}
                </Text>
                <Name size='title' fontWeight='medium'>{gift.giftItemName}</Name>
                <Row fullWidth justify='flex-start' align='center' spacing={1}>
                    <Text size='body-long' color={colors.textSecondary}>
                        {t('giftBox.sentAt', { date: formatGiftDate(gift.createdAt) })}
                    </Text>
                    {statusLabel && <Badge>{statusLabel}</Badge>}
                </Row>
            </Info>
        </Item>
    );
}

const Item = styled.button`
    display: flex;
    align-items: center;
    gap: var(--spacing-4);
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${colors.primary};
        outline-offset: 2px;
    }
`;

const Thumb = styled(Image)`
    flex-shrink: 0;
    border-radius: 4px;
    object-fit: contain;
`;

const Info = styled(Column)`
    min-width: 0;
`;

const Name = styled(Text)`
    overflow-wrap: anywhere;
`;

export default SentGiftItem;
